import type { ProjectExportOptions } from '@canvas/shared-types';
import type { fabric } from 'fabric';

export type RasterExportFormat = 'png' | 'jpg';
export type ExportFormat = RasterExportFormat | 'svg' | 'pdf';

function sanitizeFilename(name: string): string {
  const cleaned = name
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return cleaned || 'craftara-export';
}

function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function setBackgroundColorAsync(
  canvas: fabric.Canvas,
  color: string | fabric.Pattern | fabric.Gradient
): Promise<void> {
  return new Promise((resolve) => {
    canvas.setBackgroundColor(color as string, () => resolve());
  });
}

/**
 * Temporarily clear artboard fill (and background image) for transparent exports.
 * Restores previous state afterward. Does not touch object fills.
 */
export async function withTransparentArtboard<T>(
  canvas: fabric.Canvas,
  transparent: boolean,
  fn: () => T | Promise<T>
): Promise<T> {
  if (!transparent) {
    return await fn();
  }

  const prevBg = canvas.backgroundColor;
  const prevBgImage = canvas.backgroundImage;
  try {
    await setBackgroundColorAsync(canvas, '');
    if (prevBgImage) {
      canvas.backgroundImage = undefined as unknown as fabric.Image;
    }
    canvas.requestRenderAll();
    return await fn();
  } finally {
    await setBackgroundColorAsync(canvas, (prevBg as string) || '');
    if (prevBgImage) {
      canvas.backgroundImage = prevBgImage;
    }
    canvas.requestRenderAll();
  }
}

function canvasToSvgString(canvas: fabric.Canvas): string {
  return canvas.toSVG({
    width: String(canvas.getWidth()),
    height: String(canvas.getHeight()),
    viewBox: {
      x: 0,
      y: 0,
      width: canvas.getWidth(),
      height: canvas.getHeight(),
    },
  });
}

/**
 * Export the Fabric artboard as SVG and trigger a browser download.
 */
export async function exportFabricSvg(
  canvas: fabric.Canvas,
  filename?: string,
  transparentBackground = false
): Promise<void> {
  const base = sanitizeFilename(filename || 'craftara-export');
  await withTransparentArtboard(canvas, transparentBackground, () => {
    const svg = canvasToSvgString(canvas);
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    downloadBlob(blob, `${base}.svg`);
  });
}

/**
 * Export the Fabric artboard as PNG, JPG, or SVG and trigger a browser download.
 */
export async function exportFabricCanvas(
  canvas: fabric.Canvas,
  options: Pick<ProjectExportOptions, 'format' | 'quality' | 'scale' | 'filename'> & {
    format: ExportFormat;
    transparentBackground?: boolean;
  }
): Promise<void> {
  const transparent =
    Boolean(options.transparentBackground) &&
    (options.format === 'png' || options.format === 'svg');

  if (options.format === 'svg') {
    await exportFabricSvg(canvas, options.filename, transparent);
    return;
  }

  const format = options.format === 'jpg' ? 'jpeg' : 'png';
  const quality = options.quality ?? (format === 'jpeg' ? 0.92 : 1);
  const multiplier = options.scale ?? 1;
  const base = sanitizeFilename(options.filename || 'craftara-export');
  const ext = options.format === 'jpg' ? 'jpg' : 'png';

  await withTransparentArtboard(canvas, transparent, async () => {
    const prevBg = canvas.backgroundColor;
    // JPG has no alpha — ensure solid white behind transparent pixels
    if (format === 'jpeg' && (!prevBg || prevBg === 'transparent' || prevBg === '')) {
      await setBackgroundColorAsync(canvas, '#ffffff');
    }

    const dataUrl = canvas.toDataURL({
      format,
      quality,
      multiplier,
      enableRetinaScaling: true,
    });

    if (format === 'jpeg' && prevBg !== canvas.backgroundColor) {
      await setBackgroundColorAsync(canvas, (prevBg as string) || '');
      canvas.requestRenderAll();
    }

    downloadDataUrl(dataUrl, `${base}.${ext}`);
  });
}

export const OPEN_EXPORT_EVENT = 'craftara:open-export';

export function requestOpenExport(): void {
  window.dispatchEvent(new CustomEvent(OPEN_EXPORT_EVENT));
}
